import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useStore } from "zustand";
import { globalStore } from "../../stores/globalStore";
import { MdArrowForwardIos, MdEdit } from "react-icons/md";
import "./UserProfile.css";
import Service from "services/service";
import MyOrders from "component/myOrders/MyOrders";
import MyWiselist from "component/myWishlist/MyWiselist";
import MyAddress from "component/myAddress/MyAddress";
import { showErrorToast, showSuccessToast } from "utils/toastNotification";
import useAuthStore from "stores/useAuthStore";
import { formatDateDDMMYYYY } from "utils/dateFormat";
import avto from "../../assets/Images/avator.png";

const UserProfile = () => {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const { logout } = useAuthStore();
  const fileRef = useRef(null);
  const {
    profileData,
    setProfileData,
    setUpdateProfileStatus,
    getOrdersData,
    setGetOrdersData,
  } = useStore(globalStore);

  const [activeSection, setActiveSection] = useState("profile");

  useEffect(() => {
    if (routeLocation.state?.section) {
      setActiveSection(routeLocation.state.section);
    }
  }, [routeLocation.state]);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [dateOfBirth, setDob] = useState("");
  const [gender, setGender] = useState("");
  const [address, setAddress] = useState("");
  const [image, setImage] = useState(null);

  // Function to validate all fields
  const validateFields = () => {
    if (!fullName.trim()) {
      showErrorToast("Please enter your full name.");
      return false;
    }
    if (!email.trim()) {
      showErrorToast("Please enter your email.");
      return false;
    }
    if (!mobileNumber.trim()) {
      showErrorToast("Please enter your mobile number.");
      return false;
    }
    if (!dateOfBirth.trim()) {
      showErrorToast("Please enter your date of birth.");
      return false;
    }
    if (!gender.trim()) {
      showErrorToast("Please select your gender.");
      return false;
    }
    if (!address.trim()) {
      showErrorToast("Please enter your address.");
      return false;
    }
    if (!image) {
      showErrorToast("Please upload an image.");
      return false;
    }
    return true;
  };

  const getProfileData = async () => {
    try {
      const response = await Service.getProfile();
      const data = response.data.data;

      setProfileData(data);
      setUpdateProfileStatus(false);

      setFullName(data.fullName || "");
      setEmail(data.email || "");
      setMobileNumber(data.mobileNumber || "");
      setDob(
        data.dateOfBirth
          ? new Date(data.dateOfBirth).toISOString().slice(0, 10)
          : ""
      );
      setGender(data.gender || "");
      setAddress(data.address || "");
      setImage(data.image || avto); // Set image from backend or use default avatar
    } catch (error) {
      console.error("Error fetching profile data:", error);
    }
  };

  useEffect(() => {
    getProfileData();
  }, [setProfileData, setUpdateProfileStatus]);

  const fetchOrderData = async () => {
    try {
      const response = await Service.getAllOrders();
      if (response) {
        setGetOrdersData({
          OrderDetails: response.data.data,
        });
      } else {
        console.error("No data in API response:", response);
      }
    } catch (error) {
      console.error("Error fetching order data:", error);
    }
  };

  useEffect(() => {
    fetchOrderData();
  }, []);

  const handleSaveImage = async () => {
    // Validate fields before saving
    if (!validateFields()) {
      return;
    }

    const formData = new FormData();
    formData.append("fullName", fullName);
    formData.append("mobileNumber", mobileNumber);
    formData.append("dateOfBirth", dateOfBirth);
    formData.append("gender", gender);
    formData.append("address", address);
    formData.append("image", image); // Append image file directly

    try {
      const response = await Service.updateProfile(formData);
      console.log(response.data.data);
      setProfileData(response.data.data);
      setUpdateProfileStatus("success");

      showSuccessToast("Profile Updated Successfully!");
      await getProfileData();
      navigate("/UserProfile");
    } catch (error) {
      console.error("Error updating profile image:", error);
      setUpdateProfileStatus("error");
      showErrorToast("Failed to update profile. Please try again.");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImage(file); // Update the image state with the selected file
  };

  // Function to render the active section
  const renderActiveSection = () => {
    switch (activeSection) {
      case "orders":
        return <MyOrders />;
      case "wishlist":
        return <MyWiselist />;
      case "address":
        return <MyAddress />;
      case "profile":
      default:
        return (
          <div className="rowfullname">
            <div className="userProfileColumn_three">
              <div className="columnfullname">
                <p className="fullname ui text size-text3xl">Full Name</p>
                <label className="fullname-1 ui input blue_gray_50 size-lg fill">
                  <input
                    name="fullName"
                    className="profile_Phone_number"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    type="text"
                    required
                  />
                </label>
              </div>
              <div className="rowemailid">
                <div className="columnemailid">
                  <p className="fullname ui text size-text3xl">Email Id</p>
                  <input
                    name="email"
                    className="profile_Phone_number"
                    value={email}
                    placeholder="user44@gmail.com"
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                    disabled
                    required
                  />
                </div>
                <div className="columnmobilenum">
                  <p className="fullname ui text size-text3xl">Mobile Number</p>
                  <input
                    name="mobileNumber"
                    value={mobileNumber}
                    className="profile_Phone_number"
                    placeholder="+91 9832140247"
                    onChange={(e) => setMobileNumber(e.target.value)}
                    type="text"
                    required
                  />
                </div>
              </div>
              <div className="columnsave">
                <div className="rowemailid">
                  <div className="columndateofbir">
                    <p className="fullname ui text size-text3xl">
                      Date of Birth
                    </p>
                    <div className="calendar">
                      <p className="dob-display">{dateOfBirth ? formatDateDDMMYYYY(dateOfBirth) : "DD/MM/YYYY"}</p>
                      <input
                        name="calendar"
                        className="profile_Phone_number"
                        value={dateOfBirth}
                        onChange={(e) => setDob(e.target.value)}
                        type="date"
                        required
                      />
                    </div>
                  </div>
                  <div className="columndateofbir">
                    <p className="fullname ui text size-text3xl">Gender</p>
                    <div className="gender-options">
                      <label className="label_gender_profile">
                        Male
                        <div>
                          <input
                            type="radio"
                            name="gender"
                            value="Male"
                            checked={gender === "Male"}
                            onChange={() => setGender("Male")}
                            required
                          />
                        </div>
                      </label>
                      <label className="label_gender_profile">
                        Female
                        <div>
                          <input
                            type="radio"
                            name="gender"
                            value="Female"
                            checked={gender === "Female"}
                            onChange={() => setGender("Female")}
                            required
                          />
                        </div>
                      </label>
                      <label className="label_gender_profile">
                        Others
                        <div>
                          <input
                            type="radio"
                            name="gender"
                            value="Others"
                            checked={gender === "Others"}
                            onChange={() => setGender("Others")}
                            required
                          />
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="columnfullname">
                  <p className="fullname ui text size-text3xl">Address</p>
                  <textarea
                    className="frame127 ui textarea blue_gray_50 size-xs tarfillbluegray50 round"
                    name="address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="69 Banamali Naskar Road, Behala, Kolkata, WB-700034, India"
                    required
                  />
                </div>
                <button
                  onClick={handleSaveImage}
                  className="flex-row-center-center save ui button deep_orange_700_01 size-5xl fill"
                >
                  SAVE
                </button>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="user-profile-screen">
      <div className="column_four">
        <div className="rowthumbsup_one">
          <div className="column_two">
            <div className="column_one-1">
              <div className="userProfileColumn-1">
                <div className="columnthumbsup">
                  <img
                    src={
                      image instanceof File
                        ? URL.createObjectURL(image)
                        : image || avto
                    }
                    alt="Profile"
                    className="thumbsup_one"
                  />
                  <div
                    className="edit-icon"
                    onClick={() => fileRef.current.click()} // Trigger file input click
                  >
                    <MdEdit size={20} color="#fff" />
                  </div>
                </div>
                <input
                  type="file"
                  name="Profile-image"
                  className="hidden"
                  accept="image/*"
                  ref={fileRef}
                  onChange={handleFileChange}
                />
              </div>
              <div className="columnrayandipb">
                <h1 className="enterotp ui heading size-headinglg">
                  {fullName}
                </h1>
                <div className="rowemail">
                  <p className="email ui text size-textxs">{email} </p>
                  <div className="row91983214027">
                    <p className="class-91983214027 ui text size-textxs">
                      {mobileNumber}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="columnmyorders">
              <div
                className={`rowmyorders ${
                  activeSection === "profile" ? "active-section" : ""
                }`}
                onClick={() => setActiveSection("profile")}
              >
                <p className="myorders ui text size-textmd">My Profile</p>
                <MdArrowForwardIos />
              </div>

              <div
                className={`rowmyorders ${
                  activeSection === "orders" ? "active-section" : ""
                }`}
                onClick={() => setActiveSection("orders")}
              >
                <p className="myorders ui text size-textmd">My Orders</p>
                <MdArrowForwardIos />
              </div>

              <div
                className={`rowmyorders ${
                  activeSection === "address" ? "active-section" : ""
                }`}
                onClick={() => setActiveSection("address")}
              >
                <p className="myorders ui text size-textmd">My Address</p>
                <MdArrowForwardIos />
              </div>


              <div
                className={`rowmyorders ${
                  activeSection === "wishlist" ? "active-section" : ""
                }`}
                onClick={() => setActiveSection("wishlist")}
              >
                <p className="myorders ui text size-textmd">Wishlist</p>
                <MdArrowForwardIos />
              </div>
              <div
                className="rowmyorders"
                onClick={() => {
                  logout();
                  navigate("/signup");
                }}
              >
                <p className="myorders ui text size-textmd">Logout</p>
                <MdArrowForwardIos />
              </div>
            </div>
          </div>

          {/* Main Content Section */}
          {renderActiveSection()}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
